import { LayoutComponent } from '../LayoutComponent';
import { Content, Flex, Link, ProgressCircle, TextArea } from '@adobe/react-spectrum';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ToastQueue } from '@adobe/react-spectrum';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { FPADataTypes } from '../../../../infra/protected/FPA/FPAData';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { AppDispatch } from '../../../../state/store';
import { updateSelectedItemComment } from '../../../../state/Finder/finderSlice';
import { GetActivityRequest } from '../../../../services/soap/project/requests/GetActivityRequest';
import { GetProjectRequest } from '../../../../services/soap/project/requests/GetProjectRequest';
import { GetFolderRequest } from '../../../../services/soap/project/requests/GetFolderRequest';
import { UpdateProjectRequest } from '../../../../services/soap/project/requests/UpdateProjectRequest';
import { UpdateFolderRequest } from '../../../../services/soap/project/requests/UpdateFolderRequest';
import { UpdateActivityRequest } from '../../../../services/soap/project/requests/UpdateActivityRequest';
import styles from './ContextCommentComponent.module.css';

export interface IContextCommentComponentProps {
  commentTitle?: string;
  selectedItem?: any;
}

function ContextComment({ commentTitle, selectedItem }: IContextCommentComponentProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [disabled, setDisabled] = useState(true);
  const [comment, setComment] = useState('');
  const item_info = useRef<any>(null);
  const { projectService, store } = useDependency();
  const [showLoader, setShowLoader] = useState<boolean>(true);
  // const selectedItem = useSelector((state: RootState) => state.finder.selectedItem);

  const loadComment = useCallback(async () => {
    if (selectedItem) {
      setComment('');
      setShowLoader(true);
      switch (selectedItem.type) {
        case FPADataTypes.ACTIVITY:
          let { ACTIVITY } = await projectService.getActivity(new GetActivityRequest(store.Server, store.SessionId, selectedItem.id));
          item_info.current = ACTIVITY;
          setComment(ACTIVITY?.comment || '');
          break;
        case FPADataTypes.PROJECT:
          let { PROJECT } = await projectService.getProject(new GetProjectRequest(store.Server, store.SessionId, selectedItem?.id));
          item_info.current = PROJECT;
          setComment(PROJECT?.comment || '');
          break;
        case FPADataTypes.FOLDER:
          let { FOLDER } = await projectService.getFolder(new GetFolderRequest(store.Server, store.SessionId, selectedItem.id));
          item_info.current = FOLDER;
          setComment(FOLDER?.comment || '');
          break;
      }
      setShowLoader(false);
    } else {
      setComment('');
    }
  }, [projectService, selectedItem, store.Server, store.SessionId]);

  const saveComment = useCallback(
    async (_comment: string): Promise<boolean> => {
      if (!selectedItem) return false;
      switch (selectedItem.type) {
        case FPADataTypes.ACTIVITY: {
          const activity_save = await projectService.updateActivity(
            new UpdateActivityRequest(store.Server, store.SessionId, { ...item_info.current!, comment: _comment })
          );
          if (activity_save.result !== 'OK') {
            ToastQueue.negative('Error saving comment');
            return false;
          }
          return true;
        }
        case FPADataTypes.PROJECT: {
          const project_save = await projectService.updateProject(
            new UpdateProjectRequest(store.Server, store.SessionId, { ...item_info.current!, comment: _comment })
          );
          if (project_save.result !== 'OK') {
            ToastQueue.negative('Error saving comment');
            return false;
          }
          return true;
        }
        case FPADataTypes.FOLDER: {
          const folder_save = await projectService.updateFolder(
            new UpdateFolderRequest(store.Server, store.SessionId, { ...item_info.current!, comment: _comment })
          );
          if (folder_save.result !== 'OK') {
            ToastQueue.negative('Error saving comment');
            return false;
          }
          return true;
        }
        default:
          return false;
      }
    },
    [projectService, selectedItem, store.Server, store.SessionId]
  );

  useEffect(() => {
    loadComment();
  }, [loadComment]);
  const handleSave = async () => {
    const ok = await saveComment(comment);
    if (ok) {
      dispatch(updateSelectedItemComment(comment));
      if (item_info.current) {
        item_info.current = { ...item_info.current, comment };
      }
      ToastQueue.positive(t('comment_saved_successfully', { ns: 'layout_components' }), { timeout: 3000 });
      setDisabled(true);
    }
  };

  const handleCancel = () => {
    setComment(item_info.current?.comment || '');
    setDisabled(true);
  };

  const handleClick = () => {
    setDisabled(false);
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSave();
    }
  };
  if (showLoader) {
    return (
      <Flex width="100%" justifyContent={'center'} marginTop={10}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  }
  return (
    <>
      <Flex direction={'column'} width={'100%'} position={'relative'} alignItems={'start'} justifyContent={'start'} gap={'16px'} UNSAFE_className={styles.comment_heading_parent}>
        <Content alignSelf={'stretch'} position={'relative'} UNSAFE_className={styles.commnet_heading_text}>
          {commentTitle}
        </Content>
        <Flex direction={'column'} alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'} UNSAFE_className={styles.comment_box_wrapper}>
          <Flex direction={'column'} alignItems={'start'} justifyContent={'start'} width={'100%'}>
            <div style={{ width: '100%' }} onClick={handleClick} onFocus={handleClick} onKeyUp={handleKeyUp}>
              <TextArea
                aria-label="test"
                width="100%"
                value={comment || ''}
                onChange={e => {
                  if (disabled) setDisabled(false);
                  setComment(e);
                }}
                UNSAFE_className={styles.comment_box}
              />
            </div>
          </Flex>
          {!disabled && (
            <Flex width={'100%'} marginTop={'size-200'} direction="row" gap="size-100" justifyContent={'end'}>
              <Link isQuiet onPress={handleCancel} UNSAFE_className={styles.btn_cancel}>
                {t('cancel', { ns: 'layout_components' })}
              </Link>
              <Link isQuiet onPress={handleSave} UNSAFE_className={styles.btn_confirm}>
                {t('save', { ns: 'layout_components' })}
              </Link>
            </Flex>
          )}
        </Flex>
      </Flex>
    </>
  );
}

export const ContextCommentComponent = LayoutComponent(ContextComment);
